import Bugsnag from "@bugsnag/js";
import BugsnagPluginVue from "@bugsnag/plugin-vue";
import { ApiError } from "@/api/ApiError";
import router from "@/router/router";
import { removeAccessToken } from "@/utils/storage";
import { useUserStore } from "@/store/user";

const { VITE_ENV, VITE_VERSION, VITE_BUGSNAG_API_KEY } = import.meta.env;
const suppressedErrors = [
  "ResizeObserver loop limit exceeded",
  "ResizeObserver loop completed with undelivered notifications.", // mostly in imlive chats, it doesn't depend on us
  "Exception invoking getStackDelegated",
  "'i' is not defined",
  "signal is aborted without reason",
  "ReferenceError: i is not defined",
  "ReferenceError: Can't find variable: i",
  "Failed to fetch",
  "fetch failed",
  "Not in fullscreen mode",
  "The video is not fullscreen",
  "Fullscreen request denied",
  "Cannot read properties of null (reading 'getBoundingClientRect')",
  "null is not an object (evaluating 'e.value.getBoundingClientRect')",
  "Cannot read properties of null (reading 'addUpdateListener')",
  "Invalid target for null#on; must be a DOM node or evented object.",
  "Underlying ArrayBuffer has been detached from the view or out-of-bounds",
  "Failed to execute 'exitFullscreen' on 'Document': Document not active",
  "Cannot destructure property",
  "attempting to access detached ArrayBuffer",
  "Cannot perform Construct on a detached ArrayBuffer",
  "undefined is not an object (evaluating 'e.touches[0]')",
  "Cannot read properties of null (reading 'currentTime')",
  "vue error handler received a non-error",
  "Illegal invocation",
  "Fetch is aborted",
  "Unable to preload CSS",
  "Access is denied for this document",
  "The operation is insecure.",
  "Failed to read the 'sessionStorage' property from 'Window': Access is denied for this document.",
  "Can't find variable: IntersectionObserver",
  "Can't find variable: CONFIG",
  "The operation is not supported.",
  "The request is not allowed by the user agent or the platform in the current context, possibly because the user denied permission.",
  "Maximum call stack size exceeded.",
  "Not in fullscreen",
];

let bugsnagStarted = false;
let unhandledRejectionListenerAttached = false;

function attachUnhandledRejectionListener() {
  if (unhandledRejectionListenerAttached) {
    return;
  }

  unhandledRejectionListenerAttached = true;

  window.addEventListener("unhandledrejection", (e: PromiseRejectionEvent) => {
    e.preventDefault();
    const status = e.reason?.status?.status;

    if (status !== 401) {
      if (e.reason instanceof ApiError && status > 500) {
        Bugsnag.notify(e.reason);
      } else {
        console.debug(e);
      }
    } else {
      const userStore = useUserStore();
      const profile = userStore.getProfile;

      if (profile) {
        removeAccessToken();
        userStore.logout({
          path: "/#log_in",
          query: { redirect: router.currentRoute.value.fullPath },
        });
      }
    }
  });
}

export const startBugsnag = () => {
  if (bugsnagStarted || Bugsnag.isStarted()) {
    bugsnagStarted = true;
    return;
  }

  Bugsnag.start({
    apiKey: VITE_BUGSNAG_API_KEY as string,
    appVersion: VITE_VERSION,
    releaseStage: VITE_ENV,
    enabledReleaseStages: ["production", "staging"],
    autoTrackSessions: false,
    plugins: [new BugsnagPluginVue()],
    onError: async function (event) {
      const message = event.originalError?.message || event.originalError || "";

      if (suppressedErrors.indexOf(message) > -1 || !event.errors[0].errorMessage || message.length < 3) {
        return false;
      }

      if (event.originalError?.status?.status >= 400) {
        return false;
      }

      const profile = useUserStore().getProfile;

      if (profile) {
        event.setUser(profile.id.toString());
      }

      event.addMetadata("error", {
        message,
        status: event.originalError?.status,
      });
    },
  });

  window.bugsnag = Bugsnag;

  attachUnhandledRejectionListener();

  bugsnagStarted = true;
};

export default Bugsnag;
